<template>
  <div class="dish-page">
      <div class="page_button_header">
        <router-link to="/list/" class="button back">
          <font-awesome-icon :icon="['fas', 'chevron-left']" class="fa-xs" />
          Back
        </router-link>
        <div v-if="dish">
          <button id="edit_dish_button" @click="toggleEditDishForm">
            <font-awesome-icon :icon="['fas', 'pen']" />
          </button>
        </div>
      </div>
    <template v-if="dish">

      <div class="dish-details">
        <div>
          <h1 class="dish-title">{{ dish.title }}</h1>
          <p v-if="dish.description.length > 0" class="dish-description">{{ dish.description }}</p>
          <p class="tag">{{ getListName(dish.list) }}</p>
        </div>

        <div>
          <p class="medium">Exposure Goal</p>
          <div class="exposure_progress">
            <div
              class="exposure_progress_inner"
              :class="{ 'zero-width': progress === 0 }"
              :style="{ width: progress + '%' }"
            ></div>
          </div>
          <p class="progress_numbers">{{ dish.logs.length }} of {{ dish.goal }} exposures</p>
        </div>

        <div v-if="dish.logs.length > 0">
          <p class="medium">Last exposure</p>
          <p class="last_exposure_text">{{ getLastExposure() }}</p>
        </div>

        <div v-if="dish.logs.length > 0" class="anxiety_level_over_time_container">
          <p class="medium">Anxiety Level over Time</p>
          <div>
            <LineChart :labels="anxietyData.labels" :dataPoints="anxietyData.dataPoints" />
          </div>
        </div>

        <div>
          <div class="flex_row">
            <p class="medium">Exposure Logs</p>
            <button class="button secondary add_log_button" @click="toggleAddExposureLogForm">
              <font-awesome-icon :icon="['fas', 'plus']" />
            </button>
          </div>
          <ExposureList :logs="dish.logs" :dish_id="dish.id" />
        </div>
      </div>
      <AddExposureLogForm @logAdded="logAdded" @hideForm="toggleAddExposureLogForm" :dishId="dish.id" :show="showAddExposureLogForm" />
      <EditDishForm @dishUpdated="dishUpdated" @hideForm="toggleEditDishForm" :show="showEditDishForm" :title="dish.title" :description="dish.description" :goal="dish.goal" :dishId="dish.id" :list="dish.list" />
    </template>

    <template v-else>
      <div v-show="loading" class="loader"></div>
      <div v-if="!dish && fetchError" class="error-message">
        <p>{{ fetchError }}</p>
        <button @click="fetchDish(id)">Retry</button>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import ExposureList from '@/components/ExposureList.vue'
import AddExposureLogForm from '@/components/AddExposureLogForm.vue'
import EditDishForm from '@/components/EditDishForm.vue'
import LineChart from '@/components/LineChart.vue'
import { useStore } from 'vuex'
import { fetchWithAuth } from '@/utils/api'
import { type ExposureLog, type Dish } from '@/types';

const store = useStore();
const apiUrl = store.getters.getApiUrl;
const route = useRoute();
const id = route.params.dish_id as string;


const showAddExposureLogForm = ref(false);
const showEditDishForm = ref(false);
const loading = ref(false);
const fetchError = ref<string | null>(null);

const getListName = (listId: string) => {
  switch (listId) {
    case 'current':
      return 'Current Exposure';
    case 'goals':
      return 'Target Dishes';
    case 'archived':
      return 'Archived Dishes';
    case 'safe':
      return 'Safe Foods';
    case 'no-go':
      return 'No-Go List';
    default:
      return 'Unknown List';
  }
}

const anxietyData = computed(() => {
  if (!dish.value || !dish.value.logs.length) return { labels: [], dataPoints: [] }

  const sortedLogs = [...dish.value.logs].sort((a, b) =>
    new Date(a.date).getTime() - new Date(b.date).getTime()
  )

  const labels = sortedLogs.map(log =>
    new Date(log.date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })
  )

  const dataPoints = sortedLogs.map(log => log.anxiety_level ?? null)

  return { labels, dataPoints }
})

const getLastExposure = () => {
  if (!dish.value || !dish.value.logs || dish.value.logs.length === 0) {
    return 'No exposures yet';
  }

  // Sort logs by date descending
  const sortedLogs = [...dish.value.logs].sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  const lastLogDate = new Date(sortedLogs[0].date);
  const now = new Date();

  // Calculate difference in days
  const diffTime = now.getTime() - lastLogDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  const formattedDate = lastLogDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  if (diffDays === 0) return `Today - ${formattedDate}`;
  if (diffDays === 1) return `1 day ago - ${formattedDate}`;
  return `${diffDays} days ago - ${formattedDate}`;
};

const toggleAddExposureLogForm = () => {
  showAddExposureLogForm.value = !showAddExposureLogForm.value;
};

const toggleEditDishForm = () => {
  showEditDishForm.value = !showEditDishForm.value;
}

const dishUpdated = (updatedDish: {title: string, description: string, goal: number, list: string}) => {
  if ( dish.value ) {
    dish.value.title = updatedDish.title;
    dish.value.description = updatedDish.description;
    dish.value.goal = updatedDish.goal;
    dish.value.list = updatedDish.list;
  }
}

const logAdded = (new_log: ExposureLog) => {
  if ( dish.value ) {
    dish.value.logs.push({
      ...new_log
    });
  }
};

const dish = ref<Dish | null>(null)

const fetchDish = async (id: string) => {

  loading.value = true
  fetchError.value = null

  try {
    const response = await fetchWithAuth(`${apiUrl}/api/dishes/${id}`, { method: 'GET' }, store)
    if (response.ok) {
      dish.value = await response.json()
    } else {
      fetchError.value = `Failed to fetch dish!`
      console.error('Failed to fetch dish:', await response.text())
    }
    loading.value = false
  } catch (error) {
    loading.value = false
    fetchError.value = `Error fetching dish: ${error instanceof Error ? error.message : 'Unknown error'}`
    console.error('Error fetching dish:', error)
  }
}

onMounted(() => {
  fetchDish(id)
})

const progress = computed(() => {
  if (!dish.value || !dish.value.goal || dish.value.goal === 0) return 0
  return Math.min((dish.value.logs.length / dish.value.goal) * 100, 100)
})
</script>

<style scoped>

.dish-details .tag {
  margin-top: 10px;
  float: right;
}

.dish-title {
  margin-bottom: 0;
}

.page_button_header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid hsl(31, 19%, 87%);
  padding-bottom: var(--space-sm);
}

.page_button_header button {
  cursor: pointer;
}

#edit_dish_button {
  background-color: transparent;
  border: none;
  color: #f09943;
  padding: var(--space-sm);
  border-radius: 5px;
}

#edit_dish_button:hover {
  background-color: #fff5ea;
}

.dish-details {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

.exposure_progress {
  width: 100%;
  border-radius: 50px;
  background-color: #f2ece6;
  margin-top: var(--space-sm);
}

.exposure_progress_inner {
  height: 100%;
  background-color: var(--primary-color);
  border-radius: 50px;
  padding: 7px 15px;
  color: white;
  font-size: 0.8rem;
  margin-top: 5px;
  transition: width 0.3s ease;
}

.exposure_progress_inner.zero-width {
  background-color: transparent;
  color: grey;
}

.dish-description {
  font-weight: 300;
  margin-top: var(--space-sm);
  overflow-wrap: break-word;
  word-break: break-word;
}

.flex_row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.progress_numbers {
  font-size: 14px;
  margin-top: var(--space-sm);
  color: #a6917b;
}

.last_exposure_text {
  color: #a6917b;
  font-size: 14px;
  margin-top: var(--space-sm);
}

.add_log_button {
  font-size: 14px;
  background-color: transparent;
}

.add_log_button:hover {
  background-color: #fff5ea;
}

.anxiety_level_over_time_container > div {
  margin-top: var(--space-sm);
}

.dish-page .loader {
    margin-top: 20px;
}

</style>
